import { useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import staffRecordService, { errorText } from '../../services/staffRecords';
import type { StaffRecordFile } from '../../types/staffRecords';
import { openDocument } from '../../utils/openDocument';

/**
 * The files on one note or warning (a resignation letter, a signed copy), and
 * a button to attach another. HR only; the employee never sees these.
 */
export default function StaffRecordFiles({ kind, ownerId, files, onChanged, readOnly = false }: {
  kind: 'notes' | 'warnings';
  ownerId: number;
  files: StaffRecordFile[];
  onChanged: () => void;
  readOnly?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<number | null>(null);

  const upload = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      for (const f of Array.from(list)) {
        await staffRecordService.attach(kind, ownerId, f);
      }
      onChanged();
    } catch (err) {
      setError(errorText(err, 'The file could not be attached'));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const remove = async (id: number) => {
    setBusy(true);
    setError(null);
    try {
      await staffRecordService.removeFile(id);
      setConfirming(null);
      onChanged();
    } catch (err) {
      setError(errorText(err, 'The file could not be removed'));
    } finally {
      setBusy(false);
    }
  };

  const open = (f: StaffRecordFile) => {
    openDocument(staffRecordService.filePath(f.id), f.file_name)
      .catch(err => setError(errorText(err, 'The file could not be opened')));
  };

  if (readOnly && files.length === 0) return null;

  return (
    <div className="space-y-1">
      {files.map(f => (
        <div key={f.id} className="flex items-center gap-2 text-sm min-w-0">
          <Paperclip className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
          <button type="button" onClick={() => open(f)} className="text-primary-700 hover:underline min-w-0 truncate text-left">
            {f.file_name}
          </button>
          {!readOnly && (confirming === f.id ? (
            <span className="flex items-center gap-2 shrink-0">
              <button type="button" disabled={busy} onClick={() => remove(f.id)} className="text-red-700 hover:underline">
                Take it off
              </button>
              <button type="button" onClick={() => setConfirming(null)} className="text-neutral-500 hover:underline">Keep</button>
            </span>
          ) : (
            <button type="button" aria-label={`Remove ${f.file_name}`} onClick={() => setConfirming(f.id)}
              className="text-neutral-400 hover:text-red-600 shrink-0">
              <X className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
      ))}
      {!readOnly && (
        <>
          <input
            ref={input}
            type="file"
            multiple
            className="hidden"
            aria-label="Attach a file"
            accept="image/*,.pdf,.doc,.docx,.txt,.eml,.msg"
            onChange={e => upload(e.target.files)}
          />
          <button type="button" disabled={busy} onClick={() => input.current?.click()}
            className="inline-flex items-center gap-1 text-sm text-primary-700 hover:underline disabled:opacity-50">
            <Paperclip className="h-3.5 w-3.5" />
            {busy ? 'Attaching…' : 'Attach a file'}
          </button>
        </>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
